import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay from '../components/CurrencyDisplay';
import useElementToPdf from '../utils/useElementToPdf';
import UnlinkedRevenuesWidget from '../components/UnlinkedRevenuesWidget';
import { 
  Calendar, 
  TrendingUp, 
  Users, 
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  BarChart2,
  Printer
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { ar } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';

// Helper: get safe filename component for a barber id
const getBarberSafeName = (barbers, barberId) => {
  const b = barbers.find(x => x.id === barberId);
  return (b?.name || 'Barber').replace(/[^\u0600-\u06FFA-Za-z0-9_-]+/g, '_');
};

const BarberReports = () => {
  const { user, isBarber, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('weekly'); // 'weekly' or 'monthly'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [report, setReport] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const printRef = useRef(null);

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `KE_BarberReport_${getBarberSafeName(barbers, selectedBarber)}_${reportType}_${selectedDate.toISOString().slice(0,10)}`,
    pageStyle: '@page { size: A4 portrait; margin: 12mm; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }',
  });

  useEffect(() => {
    fetchBarbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBarber) {
      fetchReport();
    }
  }, [selectedBarber, selectedDate, reportType]);

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers');
      setBarbers(res.data);
      
      // إذا كان حلاق، اختر نفسه تلقائياً
      if (isBarber()) {
        const myBarber = res.data.find(b => b.user_id === user?.id || b.email === user?.email);
        if (myBarber) {
          setSelectedBarber(myBarber.id);
        }
      } else if (res.data.length > 0) {
        setSelectedBarber(res.data[0].id);
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (reportType === 'weekly') {
        // الأسبوع من السبت إلى الجمعة
        startDate = startOfWeek(selectedDate, { weekStartsOn: 6 }); // السبت
        endDate = endOfWeek(selectedDate, { weekStartsOn: 6 }); // الجمعة
      } else {
        startDate = startOfMonth(selectedDate);
        endDate = endOfMonth(selectedDate);
      }

      const res = await api.get('/barber-report', {
        params: {
          barber_id: selectedBarber,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          report_type: reportType
        }
      });
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const navigatePeriod = (direction) => {
    if (reportType === 'weekly') {
      setSelectedDate(direction === 'next' ? addWeeks(selectedDate, 1) : subWeeks(selectedDate, 1));
    } else {
      setSelectedDate(direction === 'next' ? addMonths(selectedDate, 1) : subMonths(selectedDate, 1));
    }
  };

  const getPeriodLabel = () => {
    if (reportType === 'weekly') {
      const start = startOfWeek(selectedDate, { weekStartsOn: 6 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 6 });
      return `${format(start, 'd MMM', { locale: ar })} - ${format(end, 'd MMM yyyy', { locale: ar })}`;
    } else {
      return format(selectedDate, 'MMMM yyyy', { locale: ar });
    }
  };

  const getBarberName = () => {
    const barber = barbers.find(b => b.id === selectedBarber);
    return barber?.name || '';
  };

  if (!isBarber() && !isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">تقارير الحلاق</h1>
          <p className="text-muted-foreground">عرض التقارير الأسبوعية والشهرية</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Print PDF — available to both admin and barber */}
          <Button
            onClick={handlePrint}
            disabled={!report || loading}
            variant="outline"
            className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 gap-2"
            data-testid="barber-report-print-btn"
          >
            <Printer className="w-4 h-4" />
            تنزيل PDF
          </Button>

          {/* Report Type Toggle */}
          <div className="flex bg-card border border-white/10 rounded-xl p-1">
            <Button
              data-testid="weekly-report-btn"
              onClick={() => setReportType('weekly')}
              variant={reportType === 'weekly' ? 'default' : 'ghost'}
              size="sm"
              className={`gap-2 ${reportType === 'weekly' ? 'bg-primary text-primary-foreground' : ''}`}
            >
              <CalendarDays className="w-4 h-4" />
              أسبوعي
            </Button>
            <Button
              data-testid="monthly-report-btn"
              onClick={() => setReportType('monthly')}
              variant={reportType === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              className={`gap-2 ${reportType === 'monthly' ? 'bg-primary text-primary-foreground' : ''}`}
            >
              <CalendarRange className="w-4 h-4" />
              شهري
            </Button>
          </div>
        </div>
      </div>

      {/* Barber Selector (Admin Only) */}
      {isAdmin() && (
        <div className="bg-card border border-white/10 rounded-xl p-4">
          <label className="block text-sm font-medium mb-2">اختر الحلاق</label>
          <div className="flex flex-wrap gap-2">
            {barbers.map(barber => (
              <Button
                key={barber.id}
                data-testid={`barber-select-${barber.id}`}
                onClick={() => setSelectedBarber(barber.id)}
                variant={selectedBarber === barber.id ? 'default' : 'outline'}
                size="sm"
                className={selectedBarber === barber.id ? 'bg-primary text-primary-foreground' : ''}
              >
                {barber.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Period Navigator */}
      <div className="bg-card border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <Button
            data-testid="prev-period-btn"
            onClick={() => navigatePeriod('prev')}
            variant="ghost"
            size="icon"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
          
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{getPeriodLabel()}</p>
            <p className="text-sm text-muted-foreground">
              {reportType === 'weekly' ? 'من السبت إلى الجمعة' : 'التقرير الشهري'}
            </p>
          </div>
          
          <Button
            data-testid="next-period-btn"
            onClick={() => navigatePeriod('next')}
            variant="ghost"
            size="icon"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Loyalty performance comparison for this barber (shows ranking + their %
          versus other barbers, plus any of their overdue unlinked revenues) */}
      <UnlinkedRevenuesWidget />

      {/* Report Content (printable area) */}
      <div ref={printRef} className="space-y-6 bg-background text-foreground p-2 rounded">
        {/* Print-only header (visible in PDF capture) */}
        <div className="hidden print:block print:mb-4 text-center">
          <h2 className="text-xl font-bold">{getBarberName() || 'تقرير الحلاق'}</h2>
          <p className="text-sm text-muted-foreground">{getPeriodLabel()} — {reportType === 'weekly' ? 'تقرير أسبوعي' : 'تقرير شهري'}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : report ? (
          <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-primary/30 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <DirhamIconColored className="w-6 h-6 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">إجمالي الإيرادات</p>
              <p className="text-2xl font-bold text-primary"><CurrencyDisplay amount={report.total_revenue} className="text-primary" /></p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-sm text-muted-foreground">عدد العملاء</p>
              <p className="text-2xl font-bold text-blue-500">{report.total_clients}</p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-sm text-muted-foreground">متوسط لكل عميل</p>
              <p className="text-2xl font-bold text-green-500">
                <CurrencyDisplay amount={report.total_clients > 0 ? report.total_revenue / report.total_clients : 0} className="text-green-500" />
              </p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-purple-500" />
              </div>
              <p className="text-sm text-muted-foreground">أيام العمل</p>
              <p className="text-2xl font-bold text-purple-500">{report.working_days}</p>
            </div>
          </div>

          {/* Daily Breakdown */}
          {report.daily_breakdown && report.daily_breakdown.length > 0 && (
            <>
              {/* Revenue Chart */}
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-4 text-primary flex items-center gap-2">
                  <BarChart2 className="w-5 h-5" />
                  الرسم البياني للإيرادات
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={report.daily_breakdown} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="day_name" 
                        stroke="#888" 
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#888" 
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={(value) => `${value}`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1d', 
                          border: '1px solid #D4AF37',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value) => [`${value.toFixed(2)}`, 'الإيراد']}
                        labelFormatter={(label) => `يوم ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#D4AF37" 
                        strokeWidth={3}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Clients Chart */}
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-4 text-blue-500 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  عدد العملاء
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.daily_breakdown} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="day_name" 
                        stroke="#888" 
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#888" 
                        fontSize={12}
                        tickLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1d', 
                          border: '1px solid #3b82f6',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value) => [`${value} عميل`, 'العملاء']}
                        labelFormatter={(label) => `يوم ${label}`}
                      />
                      <Bar 
                        dataKey="clients" 
                        fill="#3b82f6" 
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily Table */}
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-4 text-primary">تفاصيل يومية</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="py-3 px-4 text-start text-sm font-semibold">اليوم</th>
                        <th className="py-3 px-4 text-center text-sm font-semibold">التاريخ</th>
                        <th className="py-3 px-4 text-center text-sm font-semibold">الإيراد</th>
                        <th className="py-3 px-4 text-center text-sm font-semibold">العملاء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.daily_breakdown.map((day) => (
                        <tr key={day.date || day.day_name} className="border-b border-white/5 hover:bg-secondary/20">
                          <td className="py-3 px-4 font-medium">{day.day_name}</td>
                          <td className="py-3 px-4 text-center text-muted-foreground">{day.date}</td>
                          <td className="py-3 px-4 text-center text-primary font-semibold"><CurrencyDisplay amount={day.revenue} className="text-primary" /></td>
                          <td className="py-3 px-4 text-center">{day.clients}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Best Day */}
          {report.best_day && (
            <div className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/30 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">أفضل يوم</p>
                  <p className="text-xl font-bold">{report.best_day.day_name} - {report.best_day.date}</p>
                  <p className="text-primary font-semibold"><CurrencyDisplay amount={report.best_day.revenue} className="text-primary" /> ({report.best_day.clients} عميل)</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-xl p-12 text-center">
          <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">لا توجد بيانات لهذه الفترة</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default BarberReports;
